import { BaseApiClient } from './base-client'
import {
  API_ROUTES,
  interpolateRoute,
  ApiResponse,
  PublicApiScope,
} from '@super/shared-types'

export interface ApiKey {
  id: string
  companyId: string
  name: string
  type: 'INTERNAL'
  keyLastFour: string | null
  scopes: string[]
  active: boolean
  createdAt: string
  updatedAt: string
}

export interface ApiKeyListItem {
  id: string
  companyId: string
  name: string
  type: 'INTERNAL'
  keyLastFour: string | null
  scopes: string[]
  active: boolean
  createdAt: string
  updatedAt: string
}

export interface CreateApiKeyResponse extends ApiKey {
  apiKey: string // Only returned on creation
}

export interface CreateApiKeyRequest {
  name: string
  scopes?: PublicApiScope[]
}

export interface UpdateApiKeyRequest {
  name?: string
  scopes?: PublicApiScope[]
}

export interface GrantableScopesResponse {
  scopes: PublicApiScope[]
}

export class ApiKeysClient extends BaseApiClient {
  /**
   * Create a new API key
   * Returns the full key (only time it's visible)
   * For admins, can specify companyId in request body
   */
  async createApiKey(
    request: CreateApiKeyRequest,
  ): Promise<CreateApiKeyResponse> {
    const response = await this.request<ApiResponse<CreateApiKeyResponse>>(
      API_ROUTES.API_KEYS.CREATE,
      {
        method: 'POST',
        body: JSON.stringify(request),
      },
    )
    return response.data!
  }

  /**
   * List all API keys for the current company
   * Key values are not included
   */
  async listApiKeys(): Promise<ApiKeyListItem[]> {
    const response = await this.request<ApiResponse<ApiKeyListItem[]>>(
      API_ROUTES.API_KEYS.LIST,
      {
        method: 'GET',
      },
    )
    return response.data ?? []
  }

  /**
   * Update an API key's name or scopes — applies immediately
   */
  async updateApiKey(
    id: string,
    request: UpdateApiKeyRequest,
  ): Promise<ApiKeyListItem> {
    const response = await this.request<ApiResponse<ApiKeyListItem>>(
      interpolateRoute(API_ROUTES.API_KEYS.UPDATE, { id }),
      {
        method: 'PATCH',
        body: JSON.stringify(request),
      },
    )
    return response.data!
  }

  /**
   * Delete an API key
   */
  async deleteApiKey(id: string): Promise<void> {
    await this.request(interpolateRoute(API_ROUTES.API_KEYS.DELETE, { id }), {
      method: 'DELETE',
    })
  }

  /**
   * Scopes the tenant may grant to a new or existing key right now.
   * Non-proxy scopes always; a proxy scope only when its vendor is connected.
   */
  async getGrantableScopes(): Promise<PublicApiScope[]> {
    const response = await this.request<ApiResponse<GrantableScopesResponse>>(
      API_ROUTES.API_KEYS.GRANTABLE_SCOPES,
      { method: 'GET' },
    )
    return response.data?.scopes ?? []
  }
}

// Create and export singleton instance
export const apiKeysClient = new ApiKeysClient()

// Import admin client for factory function
import { adminApiKeysClient } from './admin-api-keys.client'

// Factory function to get the appropriate client based on admin status
export function getApiKeysClient(isSuperAdmin: true): typeof adminApiKeysClient
export function getApiKeysClient(isSuperAdmin: false): ApiKeysClient
export function getApiKeysClient(isSuperAdmin: boolean) {
  return isSuperAdmin ? adminApiKeysClient : apiKeysClient
}
